import { MediaType } from "constants/enums";

import {
  UpdateProductRequestBody,
  UpdatedProductVariant
} from "../apis/useUpdateProduct";
import {
  ProductFormValues,
  ProductVariantFormValue
} from "../components/forms/ProductDetailsForm/ProductDetailsForm.types";
import {
  ProductBothTogetherMethod,
  ProductVideoVariant
} from "../constants/enums";
import { getMediaFilesIdsByProductVideoVariant } from "./getMediaFilesIdsByProductVideoVariant";

interface FormDataWithCompressedPreviewIdInVariants extends ProductFormValues {
  variants: Array<ProductVariantFormValue & { compressedPreviewId: string }>;
}

export const convertFormDataToUpdateRequestBody = (
  formData: FormDataWithCompressedPreviewIdInVariants,
  productId: string,
  defaultValues: ProductFormValues
): UpdateProductRequestBody => {
  const {
    description,
    isChapshopChoice,
    relatedAttributesValues,
    additionalAttributesValuesIds,
    variants,
    productVideoVariant,
    generalVideo
  } = formData;

  const isOneMainVideoVariant =
    productVideoVariant === ProductVideoVariant.ONE_MAIN_VIDEO;

  const isForEachVideoVariant =
    productVideoVariant === ProductVideoVariant.FOR_EACH_VIDEO;

  const beforeUpdateVariants = defaultValues.variants.map((variant) => ({
    name: variant.name,
    sku: variant.sku,
    shortId: variant.shortId,
    colorAttributeValueId: variant.colorAttributeValueId,
    barcodes: variant.barcodes.filter((barcode) => barcode.value),
    imagesIds: variant.images.map((image) => image.id),
    previewId: variant.preview?.id ?? null,
    compressedPreviewId: variant.compressedPreview?.id ?? null,
    badgeId: variant.badgeId,
    weight: Number(variant.weight),
    volume: Number(variant.volume),
    allowSellingBelowBuyingPrice: variant.allowSellingBelowBuyingPrice,
    buyingPrice: Number(variant.buyingPrice),
    standardPrice: Number(variant.standardPrice),
    intermediateResellerPrice: Number(variant.intermediateResellerPrice),
    confirmedResellerPrice: Number(variant.confirmedResellerPrice),
    influencerPrice: Number(variant.influencerPrice),
    merchantPrice: Number(variant.merchantPrice),
    recommendedSellingPrice: Number(variant.recommendedSellingPrice),
    discount:
      variant.activeDiscountType === "percentage"
        ? Number(variant.discount) || 0
        : 0,
    discountAmount:
      variant.activeDiscountType === "amount"
        ? Number(variant.discountAmount) || 0
        : 0,
    suggestionDiscount:
      variant.activeSuggestionDiscountType === "percentage"
        ? Number(variant.suggestionDiscount) || 0
        : 0,
    suggestionDiscountAmount:
      variant.activeSuggestionDiscountType === "amount"
        ? Number(variant.suggestionDiscountAmount) || 0
        : 0,
    commission: variant.commission ? Number(variant.commission) : undefined,
    commissionPercentage: variant.commissionPercentage
      ? Number(variant.commissionPercentage)
      : undefined,
    // Always 0: the flat influencer commission is gone from the form, and
    // sending it clears whatever an older product still carries.
    influencerCommission: 0,
    influencerCommissionPercentage: variant.influencerCommissionPercentage
      ? Number(variant.influencerCommissionPercentage)
      : undefined,
    variantIdsBoughtTogether: variant.variantsBoughtTogether.length
      ? variant.variantsBoughtTogether.map((product) => product.id)
      : undefined,
    bulkDiscounts: variant.bulkDiscounts.map((discountItem) => ({
      ...discountItem,
      discount: Number(discountItem.discount)
    })),
    id: variant.id
  }));

  const bodyVariants: UpdatedProductVariant[] = variants.map((variant) => {
    const variantIdsBoughtTogether = (
      formData.productBothTogetherMethod ===
      ProductBothTogetherMethod.NO_PRODUCTS
        ? []
        : formData.productBothTogetherMethod ===
            ProductBothTogetherMethod.ONE_SET_FOR_ALL_VARIANTS
          ? variants[0].variantsBoughtTogether
          : variant.variantsBoughtTogether
    ).map((product) => product.id);

    return {
      name: variant.name,
      shortTitle: variant.shortTitle?.trim() || null,
      sku: variant.sku,
      shortId: variant.shortId,
      colorAttributeValueId: variant.colorAttributeValueId,
      barcodes: variant.barcodes.filter((barcode) => barcode.value),
      imagesIds: getMediaFilesIdsByProductVideoVariant(
        variant.images,
        productVideoVariant
      ),
      compressedPreviewId: variant.compressedPreviewId,
      previewId: variant.preview?.id ?? null,
      videoPreviewId:
        isForEachVideoVariant &&
        variant.images.some((img) => img.mediaType === MediaType.VIDEO)
          ? variant.videoThumbnail?.id || null
          : null,
      badgeId: variant.badgeId || null,
      weight: Number(variant.weight),
      volume: Number(variant.volume),
      allowSellingBelowBuyingPrice: variant.allowSellingBelowBuyingPrice,
      buyingPrice: Number(variant.buyingPrice),
      merchantPrice: Number(variant.merchantPrice),
      standardPrice: Number(variant.standardPrice),
      intermediateResellerPrice: Number(variant.intermediateResellerPrice),
      confirmedResellerPrice: Number(variant.confirmedResellerPrice),
      influencerPrice: Number(variant.influencerPrice),
      recommendedSellingPrice: variant.recommendedSellingPrice
        ? Number(variant.recommendedSellingPrice)
        : null,
      discount:
        variant.activeDiscountType === "percentage"
          ? Number(variant.discount) || 0
          : 0,
      discountAmount:
        variant.activeDiscountType === "amount"
          ? Number(variant.discountAmount) || 0
          : 0,
      suggestionDiscount:
        variant.activeSuggestionDiscountType === "percentage"
          ? Number(variant.suggestionDiscount) || 0
          : 0,
      suggestionDiscountAmount:
        variant.activeSuggestionDiscountType === "amount"
          ? Number(variant.suggestionDiscountAmount) || 0
          : 0,
      commission: variant.commission ? Number(variant.commission) : undefined,
      commissionPercentage: variant.commissionPercentage
        ? Number(variant.commissionPercentage)
        : undefined,
      // Always 0: the flat influencer commission is gone from the form, and
      // sending it clears whatever an older product still carries.
      influencerCommission: 0,
      influencerCommissionPercentage: variant.influencerCommissionPercentage
        ? Number(variant.influencerCommissionPercentage)
        : undefined,
      variantIdsBoughtTogether: variantIdsBoughtTogether.length
        ? variantIdsBoughtTogether
        : undefined,
      bulkDiscounts: variant.bulkDiscounts.map((discountItem) => ({
        ...discountItem,
        discount: Number(discountItem.discount)
      })),
      id: variant.id
    };
  });

  beforeUpdateVariants.forEach((beforeUpdateVariant) => {
    const updatedVariantIndex = bodyVariants.findIndex(
      (bodyVariant) => bodyVariant.id === beforeUpdateVariant.id
    );

    if (updatedVariantIndex !== -1) {
      const barcodesForRemoving = beforeUpdateVariant.barcodes.filter(
        (beforeUpdateBarcode) =>
          bodyVariants[updatedVariantIndex].barcodes.every(
            (barcode) => barcode.id !== beforeUpdateBarcode.id
          )
      );

      bodyVariants[updatedVariantIndex].barcodes = [
        ...bodyVariants[updatedVariantIndex].barcodes,
        ...barcodesForRemoving.map((barcode) => ({
          ...barcode,
          isDelete: true
        }))
      ];

      const bulkDiscountsForRemoving = beforeUpdateVariant.bulkDiscounts.filter(
        (beforeUpdateDicount) =>
          bodyVariants[updatedVariantIndex].bulkDiscounts.every(
            (discount) => discount.id !== beforeUpdateDicount.id
          )
      );

      bodyVariants[updatedVariantIndex].bulkDiscounts = [
        ...bodyVariants[updatedVariantIndex].bulkDiscounts,
        ...bulkDiscountsForRemoving.map((discount) => ({
          ...discount,
          isDelete: true
        }))
      ];
    }

    if (
      bodyVariants.every(
        (bodyVariant) => bodyVariant.id !== beforeUpdateVariant.id
      )
    ) {
      bodyVariants.push({
        ...beforeUpdateVariant,
        compressedPreviewId: beforeUpdateVariant.compressedPreviewId,
        barcodes: beforeUpdateVariant.barcodes.map((barcode) => ({
          ...barcode,
          isDelete: true
        })),
        bulkDiscounts: beforeUpdateVariant.bulkDiscounts.map((discount) => ({
          ...discount,
          isDelete: true
        })),
        isDelete: true
      });
    }
  });

  return {
    description,
    isChapshopChoice,
    id: productId,
    additionalAttributesValuesIds: additionalAttributesValuesIds.map(
      (additionalAttribute) => String(additionalAttribute.valueId)
    ),
    relatedAttributesValues: relatedAttributesValues.filter(
      (attr) => attr.value
    ),
    generalVideoId: isOneMainVideoVariant ? generalVideo?.[0].id : null,
    generalVideoPreviewId: isOneMainVideoVariant
      ? formData.generalVideoThumbnail?.id || null
      : null,
    realVideoId: formData.realVideo?.[0]?.id ?? null,
    googleDriveLink: formData.googleDriveLink || null,
    variants: bodyVariants
  };
};
